// react
import { useCallback, useState } from 'react';

// mui
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

export interface SelfReferralFormPageProps {
  onBack: () => void;
  onSubmit: (data: {
    email: string;
    personReceivingCare: string;
    careRecipientFirstName: string;
    careRecipientLastName: string;
  }) => void | Promise<void>;
  isSubmitting?: boolean;
}

export const SelfReferralFormPage = ({
  onBack,
  onSubmit,
  isSubmitting = false,
}: SelfReferralFormPageProps) => {
  const [email, setEmail] = useState('');
  const [personReceivingCare, setPersonReceivingCare] = useState('');
  const [careRecipientFirstName, setCareRecipientFirstName] = useState('');
  const [careRecipientLastName, setCareRecipientLastName] = useState('');
  const [touched, setTouched] = useState(false);

  const isValid = email.trim().length > 0;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setTouched(true);
      if (!isValid) return;
      await onSubmit({
        email: email.trim(),
        personReceivingCare,
        careRecipientFirstName: careRecipientFirstName.trim(),
        careRecipientLastName: careRecipientLastName.trim(),
      });
    },
    [
      email,
      personReceivingCare,
      careRecipientFirstName,
      careRecipientLastName,
      isValid,
      onSubmit,
    ],
  );

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title='Invite Yourself as an End User'
            description="This will create a separate personal account for you to use KareHero's services for yourself or your family."
          />
        }
        contentSlot={
          <Box component='form' onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <TextField
                label='Email'
                type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete='email'
                error={touched && !email.trim()}
                disabled={isSubmitting}
              />

              <Alert severity='warning'>
                Use a personal email address, not your work email. This keeps
                personal care data separate from your professional account.
              </Alert>

              <FormControl fullWidth disabled={isSubmitting}>
                <InputLabel id='person-receiving-care-label'>
                  Person Receiving Care
                </InputLabel>
                <Select
                  labelId='person-receiving-care-label'
                  value={personReceivingCare}
                  label='Person Receiving Care'
                  onChange={(e) => setPersonReceivingCare(e.target.value)}
                >
                  <MenuItem value='myself'>Myself</MenuItem>
                  <MenuItem value='someone_else'>Someone Else</MenuItem>
                </Select>
              </FormControl>

              {personReceivingCare === 'someone_else' && (
                <>
                  <TextField
                    label="Care Recipient's First Name"
                    value={careRecipientFirstName}
                    onChange={(e) => setCareRecipientFirstName(e.target.value)}
                    fullWidth
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Care Recipient's Last Name"
                    value={careRecipientLastName}
                    onChange={(e) => setCareRecipientLastName(e.target.value)}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </>
              )}

              <Alert severity='info'>
                You'll receive a magic link to set up your personal account.
              </Alert>

              <Button
                type='submit'
                variant='contained'
                fullWidth
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? 'Sending…' : 'Send me an invitation'}
              </Button>
            </Stack>
          </Box>
        }
      />
    </ModalPageTemplate>
  );
};

SelfReferralFormPage.displayName = 'SelfReferralFormPage';
export default SelfReferralFormPage;
